"use client";

import React, { useEffect, useRef, useState } from "react";
import { Dumbbell, Users, Calendar, Award } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const metricsData = [{
  id: 1,
  value: 1200,
  suffix: "+",
  label: "Líderes Mentorados",
  icon: Users
}, {
  id: 2,
  value: 25,
  suffix: "+",
  label: "Anos de Mercado",
  icon: Calendar
}, {
  id: 3,
  value: 85,
  suffix: "%",
  label: "Retenção Executiva",
  icon: Award
}, {
  id: 4,
  value: 150,
  suffix: "+",
  label: "Empresas Atendidas",
  icon: Dumbbell
}];
function MetricCard({
  item,
  index
}: {
  item: (typeof metricsData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const duration = 2000;
      const percentage = Math.min(progress / duration, 1);
      const easeOut = 1 - Math.pow(1 - percentage, 4);
      const currentCount = Math.floor(easeOut * item.value);
      setCount(currentCount);
      if (progress < duration) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(item.value);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, item.value]);
  return <div data-index={index} ref={ref} className="h-full">
      <Card className="h-full bg-card rounded-sm border border-border/60 shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1">
        <CardContent className="p-8 md:p-10 flex flex-col items-center justify-center text-center gap-4">
          <div className="p-4 bg-primary/5 rounded-full mb-2">
            <item.icon className="w-10 h-10 text-primary" strokeWidth={1} />
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-default text-5xl md:text-6xl font-bold text-foreground tracking-tighter">
              {count}
            </span>
            <span className="font-default text-4xl md:text-5xl font-bold text-primary">
              {item.suffix}
            </span>
          </div>
          <p className="font-default text-sm uppercase tracking-widest font-semibold text-muted-foreground">
            {item.label}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function PulseTransformationMetrics() {
  return;
}